import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  Menu,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import ReportsClearTab from "./ReportsClearTab";
import ReportsCancelledTab from "./ReportsCancelledTab";
import ReportsTableTab from "./ReportsTableTab";
import { getCashCollectionApi, exportCashCollectionApi, exportCashCollectionPDF } from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import { useGetApi } from "../hooks/useGetApi";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import TablePagination from "@mui/material/TablePagination";

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};
const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

// User options will be fetched from API

// Template options  
const templateOptions = [
  { label: "All Payments Template", value: "allPayments" },
  { label: "OPD Payments Template", value: "opdPayments" },
  { label: "IPD Payments Template", value: "ipdPayments" },
  { label: "Refund Template", value: "refundTemplate" },
];

// Visit Type options (matching backend: OPD, IPD, Outsider)
const visitTypeOptions = [
  { label: "OPD", value: "OPD" },
  { label: "IPD", value: "IPD" },
  { label: "Outsider", value: "Outsider" },
];

// Transaction types will be fetched from API

const paymentFilterList = [
  { label: "Invoice Payment", value: "InvoicePayment" },
  { label: "Self Payment", value: "SelfPayment" },
  { label: "Refund", value: "Refund" },
  { label: "IPD Advance", value: "IPDAdvance" },
];

const buttonData = [
  { id: 1, label: "Clear - Advance Patient Payment" },
  { id: 2, label: "Cancelled - Advance Patient Payment" },
  { id: 3, label: "Table" },
];
const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

export default function ReportsCashCollection() {
  const navigate = useNavigate();
  
  // Fetch users for dropdown
  const {
    dataList: users,
    isLoading: usersLoading,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000, page: 1 },
    dataKey: "users",
  });

  // Fetch transaction types for dropdown
  const {
    dataList: transactionList,
    isLoading: transactionTypesLoading,
  } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: {},
  });

  // Transform users to options format
  const userOptions = React.useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    return users.map(user => ({
      label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User',
      value: user._id
    }));
  }, [users]);

  // Transform transaction types to options format (flatten grouped data)
  const transactionTypeList = React.useMemo(() => {
    if (!transactionList || !Array.isArray(transactionList)) return [];
    // transactionList is grouped by transactionMode, each group has transactionTypes array
    const allTypes = [];
    transactionList.forEach(group => {
      if (group.transactionTypes && Array.isArray(group.transactionTypes)) {
        group.transactionTypes.forEach(type => {
          if (type.active !== false) { // Only show active transaction types
            allTypes.push({
              label: `${type.name || ''} (${type.paymentMethod || ''})`.trim(),
              value: type._id, // Use ObjectId as value for backend
              _id: type._id,
              name: type.name,
              paymentMethod: type.paymentMethod
            });
          }
        });
      }
    });
    return allTypes;
  }, [transactionList]);
  
  // Form data state
  const [formData, setFormData] = useState({
    searchTemplate: null,
    userName: [],
    startDate: null,
    endDate: null,
    transactionType: null,
    payment: null,
    visitType: [],
  });
  
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  // Pagination state - default to 10 rows per page like Patient page
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT); // DEFAULT_LIMIT is 10
  const [totalCount, setTotalCount] = useState(0);
  
  // Flag to track if initial search has been performed (prevents pagination useEffect from running on initial search)
  const [hasSearched, setHasSearched] = useState(false);
  
  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };
  
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  
  const handleTabClick = (index) => {
    setActiveTab(index);
  };
  
  const filteredButtons = buttonData.filter(
    (btn) => btn.paymentType === selectedPayment
  );
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // State for column visibility (filter)
  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    patientUHID: true,
    patientName: true,
    amount: true,
    invoiceNo: true,
    paymentDate: true,
    transactionType: true,
    paymentType: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Cash Collection Report',
        text: `Cash Collection Report - ${totalCount} records`,
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle PDF export
  const handlePDFExport = async () => {
    try {
      setIsExporting(true);
      
      // Get current filter parameters (matching fetchReportData params)
      const exportParams = {
        recordedBy: formData.userName?.map(user => user.value).join(','),
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        transactionType: formData.transactionType?.value || formData.transactionType?._id,
        paymentType: activeTab === 1 || activeTab === 2 
          ? "IPDAdvance" 
          : formData.payment?.value,
        visitType: formData.visitType?.map(item => item.value).join(','),
        page: 1,
        limit: 100000, // Export all records for PDF
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      // Define columns for PDF export with proper labels (matching table header order)
      const columns = [
        { header: 'SNO', dataKey: 'serialNumber' },
        { header: 'Patient UHID', dataKey: 'patientUHID' },
        { header: 'Patient Name', dataKey: 'patientName' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Invoice No', dataKey: 'invoiceNo' },
        { header: 'Payment Date', dataKey: 'date' },
        { header: 'Transaction Type', dataKey: 'transactionType' },
        { header: 'Payment Type', dataKey: 'paymentType' },
      ];

      // Prepare date range for PDF header
      const dateRange = formData.startDate && formData.endDate ? {
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      } : null;

      const result = await exportCashCollectionPDF(exportParams, columns, dateRange);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to PDF!`);
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        handlePDFExport();
        break;
      case 'Report Mail Send Scheduler':
        // TODO: Implement report mail scheduler when feature is available
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        // TODO: Implement report scheduler when feature is available
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Export functionality
  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Get current filter parameters (matching fetchReportData params)
      const exportParams = {
        recordedBy: formData.userName?.map(user => user.value).join(','),
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        // Backend expects transactionType as ObjectId, not string value
        transactionType: formData.transactionType?.value || formData.transactionType?._id,
        paymentType: activeTab === 1 || activeTab === 2 
          ? "IPDAdvance" 
          : formData.payment?.value,
        visitType: formData.visitType?.map(item => item.value).join(','),
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      // Define column order for Excel export (matching table header order)
      const columnOrder = ['serialNumber', 'patientUHID', 'patientName', 'amount', 'invoiceNo', 'date', 'transactionType', 'paymentType'];

      const result = await exportCashCollectionApi(exportParams, columnOrder);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch report data - accepts optional parameters to avoid stale closure issues
  const fetchReportData = async (currentPage = page, currentRowsPerPage = rowsPerPage, currentActiveTab = activeTab, showToast = false) => {
    try {
      setLoading(true);
      
      // Map frontend form data to backend API parameters
      const params = {
        // Backend expects recordedBy (comma-separated string)
        recordedBy: formData.userName?.map(user => user.value).join(','),
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        // Backend expects transactionType as ObjectId, not string value
        transactionType: formData.transactionType?.value || formData.transactionType?._id,
        // Apply tab-based filter: tabs 1 and 2 are for IPD Advance payments
        // Tab 1: Clear - Advance Patient Payment (IPDAdvance payments)
        // Tab 2: Cancelled - Advance Patient Payment (IPDAdvance payments)
        // Tab 3: Table (all payments based on user filters)
        paymentType: currentActiveTab === 1 || currentActiveTab === 2 
          ? "IPDAdvance" 
          : formData.payment?.value, // Backend expects paymentType
        visitType: formData.visitType?.map(item => item.value).join(','), // Backend expects comma-separated string
        page: currentPage + 1, // Backend uses 1-based pagination
        limit: currentRowsPerPage,
      };

      // Remove undefined/empty values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      console.log('Fetching cash collection data - activeTab:', currentActiveTab, 'params:', params);
      const response = await getCashCollectionApi(params);
      console.log('API Response:', response);
      
      if (response?.data) {
        // Backend returns data.payments array and data.total
        let dataArray = [];
        if (response.data.payments && Array.isArray(response.data.payments)) {
          dataArray = response.data.payments;
        } else if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else {
          dataArray = [];
        }
        
        // Set total count for pagination
        setTotalCount(response.data.total || 0);
        console.log('Processed data array:', dataArray);
        setReportData(dataArray);
        
        // Only show success message when user explicitly searches
        if (showToast) {
          toast.success("Report data loaded successfully");
        }
      } else {
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error("Failed to fetch report data");
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    if (e) {
      e.preventDefault();
    }
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.userName || formData.userName.length === 0) {
      errorFields.push('userName');
    }
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    if (!formData.transactionType) {
      errorFields.push('transactionType');
    }
    if (!formData.payment) {
      errorFields.push('payment');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    console.log('Searching with form data:', formData);
    isSearchingRef.current = true; // Mark that we're performing a search
    setPage(0); // Reset to first page on new search
    setHasSearched(true); // Mark that search has been performed
    // Fetch with page 0, current rowsPerPage, current activeTab, and showToast true for success message
    fetchReportData(0, rowsPerPage, activeTab, true);
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Refetch data when tab changes (only if user has already searched)
  useEffect(() => {
    // Only refetch if user has performed a search
    // Note: dates are not always required - user might search by other filters
    if (hasSearched) {
      console.log('Tab changed - activeTab:', activeTab);
      isSearchingRef.current = true; // Prevent pagination useEffect from running
      setPage(0); // Reset to first page when changing tabs
      // Fetch with current activeTab to get correct filtered data
      fetchReportData(0, rowsPerPage, activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Refetch data when pagination changes (only if we have search criteria and user has already searched)
  // Use refs to track previous values to detect actual changes
  const prevPageRef = useRef(page);
  const prevRowsPerPageRef = useRef(rowsPerPage);
  const isSearchingRef = useRef(false);
  
  useEffect(() => {
    // Skip if no search has been performed yet
    if (!hasSearched) {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    
    // Skip if we're currently performing a search (handleSearch is running)
    if (isSearchingRef.current) {
      isSearchingRef.current = false;
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return; // handleSearch already fetched the data
    }
    
    // Check if page or rowsPerPage actually changed
    const pageChanged = prevPageRef.current !== page;
    const rowsPerPageChanged = prevRowsPerPageRef.current !== rowsPerPage;
    
    // Only refetch if:
    // 1. User has performed a search (hasSearched flag)
    // 2. Page or rowsPerPage actually changed (not just a re-render)
    // Note: We don't require dates - user might search by other filters
    if (hasSearched && (pageChanged || rowsPerPageChanged)) {
      console.log('Pagination changed - page:', page, 'rowsPerPage:', rowsPerPage, 'prevPage:', prevPageRef.current, 'prevRowsPerPage:', prevRowsPerPageRef.current);
      // Call fetchReportData with current page, rowsPerPage, and activeTab values
      fetchReportData(page, rowsPerPage, activeTab);
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    } else {
      // Update refs even if we don't fetch (to track current state)
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Load initial data (optional - could be removed to require manual search)
  useEffect(() => {
    // Optionally fetch some initial data to test the API
    // fetchReportData(); // Commented out to require manual search
  }, []);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          {/* Search and Buttons */}
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>Collection Report</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              my: "20px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, searchTemplate: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={userOptions || []}
                  multiple
                  limitTags={2}
                  value={formData.userName}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, userName: newValue }));
                    // Clear error when field is changed
                    if (errors.includes('userName')) {
                      setErrors(errors.filter(e => e !== 'userName'));
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  loading={usersLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User Name"
                      name="userName"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('userName') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleDateChange("startDate", newValue);
                        // Clear error when field is changed
                        if (errors.includes('startDate')) {
                          setErrors(errors.filter(e => e !== 'startDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleDateChange("endDate", newValue);
                        // Clear error when field is changed
                        if (errors.includes('endDate')) {
                          setErrors(errors.filter(e => e !== 'endDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={transactionTypeList || []}
                  value={formData.transactionType}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, transactionType: newValue }));
                    // Clear error when field is changed
                    if (errors.includes('transactionType')) {
                      setErrors(errors.filter(e => e !== 'transactionType'));
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  loading={transactionTypesLoading}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    // Compare by _id if both have it
                    if (option._id && value._id) return option._id === value._id;
                    // Compare by value if both have it
                    if (option.value && value.value) return option.value === value.value;
                    // Fallback to label comparison
                    return option.label === value.label;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Transaction Type"
                      name="transactionType"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('transactionType') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={paymentFilterList || []}
                  value={formData.payment}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, payment: newValue }));
                    setSelectedPayment(newValue ? newValue.value : null);
                    // Clear error when field is changed
                    if (errors.includes('payment')) {
                      setErrors(errors.filter(e => e !== 'payment'));
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment"
                      name="payment"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('payment') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={visitTypeOptions || []}
                  multiple
                  limitTags={2}
                  value={formData.visitType}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, visitType: newValue }));
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visit Type"
                      name="visitType"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          {/* Always show the data table */}
          {true && (
            <Box
              sx={{
                border: "1px solid rgba(223, 232, 239, 1)",
                borderRadius: "8px",
                mb: "30px",
                mt: "20px",
                p: "20px",
              }}
            >
              {/* dynamic button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid grey",
                    borderRadius: "5px",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    width: "670px",
                    cursor: "pointer",
                    mb: "20px",
                  }}
                >
                  <Typography
                    onClick={() => handleTabClick(1)}
                    sx={{
                      color: "grey",
                      textDecoration: "none",
                      ...(activeTab === 1 && {
                        color: "primary.main",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      }),
                    }}
                  >
                    Clear - Advance Patient Payment
                  </Typography>
                  <Divider flexItem orientation="vertical" />
                  <Typography
                    onClick={() => handleTabClick(2)}
                    sx={{
                      color: "grey",
                      textDecoration: "none",
                      ...(activeTab === 2 && {
                        color: "primary.main",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      }),
                    }}
                  >
                    Cancelled - Advance Patient Payment
                  </Typography>
                  <Divider flexItem orientation="vertical" />
                  <Typography
                    onClick={() => handleTabClick(3)}
                    sx={{
                      color: "grey",
                      textDecoration: "none",
                      ...(activeTab === 3 && {
                        color: "primary.main",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                      }),
                    }}
                  >
                    Table
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    mb: "20px",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}>
                    Rows: {totalCount || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>

                  <Box sx={iconStyleProps} onClick={handleFilterClick}>
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "transparent",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                  <Box sx={iconStyleProps} onClick={handleShare}>
                    <ShareIcon 
                      sx={{
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                  <Box sx={iconStyleProps}>
                    <IconButton onClick={handleClick}>
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Conditional rendering based on active tab */}
              {activeTab === 1 ? (
                // Clear - Advance Patient Payment tab - show filtered data in table format
                <Paper sx={{ mt: 2 }}>
                  <Table aria-label="clear advance payment table" sx={{ minWidth: 800 }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                      <TableRow>
                        <TableCell>SNO</TableCell>
                        <TableCell>Patient UHID</TableCell>
                        <TableCell>Patient Name</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Invoice No</TableCell>
                        <TableCell>Payment Date</TableCell>
                        <TableCell>Transaction Type</TableCell>
                        <TableCell>Payment Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.map((row, index) => (
                          <TableRow key={row?._id || index}>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{row?.patientUHID || "-"}</TableCell>
                            <TableCell>{row?.patientName || "-"}</TableCell>
                            <TableCell>₹{row?.amount || "-"}</TableCell>
                            <TableCell>{row?.invoiceNo || "-"}</TableCell>
                            <TableCell>{row?.date ? new Date(row.date).toLocaleDateString() : "-"}</TableCell>
                            <TableCell>{row?.transactionType || "-"}</TableCell>
                            <TableCell>{row?.paymentType || "-"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  {/* Pagination */}
                  {totalCount > 0 && (
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  )}
                </Paper>
              ) : activeTab === 2 ? (
                // Cancelled - Advance Patient Payment tab - show filtered data in table format
                <Paper sx={{ mt: 2 }}>
                  <Table aria-label="cancelled advance payment table" sx={{ minWidth: 800 }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                      <TableRow>
                        {visibleColumns.sno && <TableCell>SNO</TableCell>}
                        {visibleColumns.patientUHID && <TableCell>Patient UHID</TableCell>}
                        {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                        {visibleColumns.amount && <TableCell>Amount</TableCell>}
                        {visibleColumns.invoiceNo && <TableCell>Invoice No</TableCell>}
                        {visibleColumns.paymentDate && <TableCell>Payment Date</TableCell>}
                        {visibleColumns.transactionType && <TableCell>Transaction Type</TableCell>}
                        {visibleColumns.paymentType && <TableCell>Payment Type</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.map((row, index) => (
                          <TableRow key={row?._id || index}>
                            {visibleColumns.sno && <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                            {visibleColumns.patientUHID && <TableCell>{row?.patientUHID || "-"}</TableCell>}
                            {visibleColumns.patientName && <TableCell>{row?.patientName || "-"}</TableCell>}
                            {visibleColumns.amount && <TableCell>₹{row?.amount || "-"}</TableCell>}
                            {visibleColumns.invoiceNo && <TableCell>{row?.invoiceNo || "-"}</TableCell>}
                            {visibleColumns.paymentDate && <TableCell>{row?.date ? new Date(row.date).toLocaleDateString() : "-"}</TableCell>}
                            {visibleColumns.transactionType && <TableCell>{row?.transactionType || "-"}</TableCell>}
                            {visibleColumns.paymentType && <TableCell>{row?.paymentType || "-"}</TableCell>}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  {/* Pagination */}
                  {totalCount > 0 && (
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  )}
                </Paper>
              ) : (
                // Table tab - show all payment data
                <Paper sx={{ mt: 2 }}>
                  <Table aria-label="cash collection table" sx={{ minWidth: 800 }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                      <TableRow>
                        {visibleColumns.sno && <TableCell>SNO</TableCell>}
                        {visibleColumns.patientUHID && <TableCell>Patient UHID</TableCell>}
                        {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                        {visibleColumns.amount && <TableCell>Amount</TableCell>}
                        {visibleColumns.invoiceNo && <TableCell>Invoice No</TableCell>}
                        {visibleColumns.paymentDate && <TableCell>Payment Date</TableCell>}
                        {visibleColumns.transactionType && <TableCell>Transaction Type</TableCell>}
                        {visibleColumns.paymentType && <TableCell>Payment Type</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.map((row, index) => (
                          <TableRow key={row?._id || index}>
                            {visibleColumns.sno && <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                            {visibleColumns.patientUHID && <TableCell>{row?.patientUHID || "-"}</TableCell>}
                            {visibleColumns.patientName && <TableCell>{row?.patientName || "-"}</TableCell>}
                            {visibleColumns.amount && <TableCell>₹{row?.amount || "-"}</TableCell>}
                            {visibleColumns.invoiceNo && <TableCell>{row?.invoiceNo || "-"}</TableCell>}
                            {visibleColumns.paymentDate && <TableCell>{row?.date ? new Date(row.date).toLocaleDateString() : "-"}</TableCell>}
                            {visibleColumns.transactionType && <TableCell>{row?.transactionType || "-"}</TableCell>}
                            {visibleColumns.paymentType && <TableCell>{row?.paymentType || "-"}</TableCell>}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  {/* Pagination */}
                  {totalCount > 0 && (
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  )}
                </Paper>
              )}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>
              
              {/* Filter Menu for Column Visibility */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => toggleColumn('sno')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>SNO</Box>
                    {visibleColumns.sno && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientUHID')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient UHID</Box>
                    {visibleColumns.patientUHID && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Name</Box>
                    {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('amount')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Amount</Box>
                    {visibleColumns.amount && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('invoiceNo')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Invoice No</Box>
                    {visibleColumns.invoiceNo && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('paymentDate')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Payment Date</Box>
                    {visibleColumns.paymentDate && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('transactionType')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Transaction Type</Box>
                    {visibleColumns.transactionType && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('paymentType')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Payment Type</Box>
                    {visibleColumns.paymentType && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
